import { useEffect, useState, MouseEvent } from "react";
import ActivityNote from "./ActivityNote.tsx";
import { compact, filter, isEmpty, isEqual, map, omit } from "lodash";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowRightArrowLeft,
  faSquare,
  faCircle,
  faSpinner,
} from "@fortawesome/pro-regular-svg-icons";
import useTooltip from "../../../../hooks/useTooltip.tsx";
import classNames from "classnames";
import { useCreateVote } from "../../../../api/activitystreams.ts";
import { useUpdateActivityInCache } from "../../../../hooks/activitystreams/useUpdateActivityInCache.ts";
import {
  Activity,
  ScheduleObject,
  SurveyObject,
} from "../../../../@types/activities.ts";
import { Controller, FormProvider, useForm } from "react-hook-form";
import SurveyOptions from "./survey/SurveyOptions.tsx";
import TranslatableTextInput from "../../../shared/multilingualism/TranslatableTextInput.tsx";
import { faCheckCircle, faCheckSquare } from "@fortawesome/pro-solid-svg-icons";
import { ScheduleField } from "../../composer/ScheduleFields.tsx";
import formatDate from "../../../../helpers/formatDate.ts";
import FormActions from "@/components/shared/form/fields/FormActions.tsx";

type SurveyFormData = {
  option_ids: string[];
  custom_option: string;
  custom_option_translations: Record<string, Record<string, string>>;
  custom_proposal: {
    date: string;
    time: string;
    time_end: string;
  };
};

type ActivitySurveyArgs = (SurveyObject | ScheduleObject) & {
  activityId: string;
  isSchedule?: boolean;
  isHit?: boolean;
  readOnly?: boolean;
};

export default function ActivitySurvey(props: ActivitySurveyArgs) {
  const {
    type,
    activityId,
    content,
    multiple_select,
    unique_voter_count,
    total_votes,
    option_adding,
    isSchedule = false,
    closed,
    anonymous,
    isHit = false,
    readOnly = false,
    ...otherProps
  } = props;
  const [showUniqueVoterCount, setShowUniqueVoterCount] = useState(false);
  const [votersTitle, setVotersTitle] = useState(
    I18n.t(
      total_votes === 1
        ? "js.activitystreams.survey.vote_count.one"
        : "js.activitystreams.survey.vote_count.other",
      { count: total_votes },
    ),
  );
  const tooltipRef = useTooltip<HTMLButtonElement>(votersTitle);

  const methods = useForm<SurveyFormData>({
    values: {
      option_ids: map(
        filter(
          type === "schedule"
            ? (otherProps as ScheduleObject).proposals
            : (otherProps as SurveyObject).options,
          "voted_for",
        ),
        "id",
      ),
      custom_option: "",
      custom_option_translations: {},
      custom_proposal: { date: "", time: "", time_end: "" },
    },
  });
  const { handleSubmit, control, watch, reset, setValue } = methods;

  const { updateActivity } = useUpdateActivityInCache();
  const { mutate: createVote, isLoading: isVoting } = useCreateVote({
    onSuccess: (updatedActivity: Activity) => {
      // No reset() here: it would restore the pre-vote selection (the user's
      // previous vote) until the cache update below re-syncs the form via
      // `useForm({ values })` — visible as a wrongly re-checked old option.
      const proposals = isSchedule
        ? { proposals: (updatedActivity.object as SurveyObject).options }
        : {};
      updateActivity(
        activityId,
        (activity) =>
          ({
            ...activity,
            object: {
              ...activity.object,
              ...omit(updatedActivity.object, [
                "replies",
                "content",
                "html_content",
              ]),
              ...proposals,
            },
          }) as Activity,
      );
    },
  });

  const selectedOptionIds = watch("option_ids");
  const customOption = watch("custom_option");
  const customProposalDate = watch("custom_proposal.date");

  const givenVotes = map(
    filter(
      isSchedule
        ? (otherProps as ScheduleObject).proposals
        : (otherProps as SurveyObject).options,
      "voted_for",
    ),
    "id",
  );

  useEffect(() => {
    setVotersTitle(
      showUniqueVoterCount
        ? I18n.t(
            total_votes === 1
              ? "js.activitystreams.survey.vote_count.one"
              : "js.activitystreams.survey.vote_count.other",
            { count: total_votes },
          )
        : I18n.t(
            unique_voter_count === 1
              ? "js.activitystreams.survey.unique_voter_count.one"
              : "js.activitystreams.survey.unique_voter_count.other",
            { count: unique_voter_count },
          ),
    );
  }, [showUniqueVoterCount, total_votes, unique_voter_count]);

  function submit(data: SurveyFormData) {
    activityId &&
      createVote({
        activityId,
        body: isSchedule
          ? {
              proposal_ids: data.option_ids,
              custom_proposal: isEmpty(data.custom_proposal.date)
                ? undefined
                : {
                    ...data.custom_proposal,
                    date: formatDate(
                      new Date(data.custom_proposal.date),
                      "dd.MM.y",
                    ),
                  },
            }
          : {
              ...data,
              custom_options: compact([data.custom_option]),
              custom_option_translations: data.custom_option_translations,
            },
      });
  }

  useEffect(() => {
    if (
      (!isEmpty(customOption) ||
        (isSchedule && !isEmpty(customProposalDate))) &&
      !multiple_select
    )
      setValue("option_ids", []);
  }, [customOption, customProposalDate]);

  function removeAnswer(e: MouseEvent<HTMLButtonElement>) {
    e.preventDefault();

    activityId &&
      createVote({
        activityId,
        body: isSchedule ? { proposal_ids: [] } : { option_ids: [] },
      });
  }

  return (
    <FormProvider {...methods}>
      <form
        className={classNames("survey-view flex flex-col", {
          "divide-neutral divide-y divide-dashed": !window.isApp,
        })}
        onSubmit={handleSubmit(submit)}
      >
        {content && <ActivityNote content={content} />}
        <div
          className={classNames("flex flex-col gap-2", {
            "mt-4": !window.isApp,
            "gap-2.5": window.isApp,
          })}
        >
          {!isSchedule && !isHit && (
            <div
              className={classNames(
                "votes-switcher flex gap-2 justify-end items-center text-xs text-muted",
                { "justify-between!": anonymous },
              )}
            >
              {anonymous && (
                <span className="font-semibold">
                  {I18n.t("js.activitystreams.survey_anonymous_message")}
                </span>
              )}
              <div>
                {showUniqueVoterCount || !multiple_select
                  ? I18n.t(
                      unique_voter_count === 1
                        ? "js.activitystreams.survey.unique_voter_count.one"
                        : "js.activitystreams.survey.unique_voter_count.other",
                      { count: unique_voter_count },
                    )
                  : I18n.t(
                      total_votes === 1
                        ? "js.activitystreams.survey.vote_count.one"
                        : "js.activitystreams.survey.vote_count.other",
                      { count: total_votes },
                    )}
                {multiple_select && (
                  <button
                    ref={tooltipRef}
                    className="btn ml-2 cursor-pointer"
                    type="button"
                    title={votersTitle}
                    onClick={() =>
                      setShowUniqueVoterCount(!showUniqueVoterCount)
                    }
                  >
                    <FontAwesomeIcon
                      icon={faArrowRightArrowLeft}
                      size={window.isApp ? "lg" : "sm"}
                    />
                  </button>
                )}
              </div>
            </div>
          )}
          <Controller
            control={control}
            render={({ field }) => (
              <SurveyOptions
                {...omit(field, "ref")} // Function components cannot be given refs
                options={
                  (otherProps as SurveyObject).options ||
                  (otherProps as ScheduleObject).proposals
                }
                multiple={multiple_select}
                showUniqueVoterCount={showUniqueVoterCount}
                isSchedule={isSchedule}
                activityId={activityId}
                anonymous={anonymous}
                closed={closed || isHit || readOnly}
              />
            )}
            name="option_ids"
          />
          {option_adding &&
            !isHit &&
            !readOnly &&
            (isSchedule ? (
              <div className="flex gap-2 items-center">
                <FontAwesomeIcon
                  className="stack-item ml-3 text-muted self-center max-w-content"
                  size={window.isApp ? "xl" : undefined}
                  icon={
                    multiple_select
                      ? isEmpty(customProposalDate)
                        ? faSquare
                        : faCheckSquare
                      : isEmpty(customProposalDate)
                        ? faCircle
                        : faCheckCircle
                  }
                />
                <ScheduleField
                  dateName="custom_proposal.date"
                  timeName="custom_proposal.time"
                  timeEndName="custom_proposal.time_end"
                  allowEmptyTime
                />
              </div>
            ) : (
              <div className="custom-option-container flex items-center gap-2">
                <FontAwesomeIcon
                  className="text-muted shrink-0"
                  size={window.isApp ? "xl" : undefined}
                  icon={
                    multiple_select
                      ? isEmpty(customOption)
                        ? faSquare
                        : faCheckSquare
                      : isEmpty(customOption)
                        ? faCircle
                        : faCheckCircle
                  }
                />
                <Controller
                  control={control}
                  name="custom_option"
                  render={({ field }) => (
                    <TranslatableTextInput
                      name={field.name}
                      onBlur={field.onBlur}
                      value={field.value}
                      translationsValue={watch("custom_option_translations")}
                      onValueChange={(label, translations) => {
                        setValue("custom_option", label);
                        setValue("custom_option_translations", translations);
                      }}
                      field="label"
                      className="form-control"
                      containerClassName="flex-1 min-w-0"
                      placeholder={I18n.t("js.activitystreams.add_option")}
                    />
                  )}
                />
              </div>
            ))}
          {!closed && !isHit && !readOnly && (
            <>
              {isEqual(selectedOptionIds, givenVotes) &&
              isEmpty(customOption) &&
              isEmpty(customProposalDate) ? (
                !multiple_select &&
                !isEmpty(givenVotes) && (
                  <div className="flex justify-end items-center gap-2 mt-4">
                    <button
                      type="button"
                      className="remove-answers btn btn-light btn-sm"
                      onClick={removeAnswer}
                    >
                      {isVoting && (
                        <FontAwesomeIcon
                          className="mr-1"
                          icon={faSpinner}
                          spin
                        />
                      )}
                      {I18n.t("js.activitystreams.remove_answer")}
                    </button>
                  </div>
                )
              ) : (
                <FormActions
                  onCancel={() => reset()}
                  saveText={I18n.t("js.activitystreams.answers.submit")}
                  saveDisabled={isVoting}
                  saveIconProps={
                    isVoting
                      ? {
                          className: "mr-1",
                          icon: faSpinner,
                          spin: true,
                        }
                      : undefined
                  }
                />
              )}
            </>
          )}
        </div>
      </form>
    </FormProvider>
  );
}
